import {useUser} from '@dofleini/security';
import {process, proposalrequest} from '@mep/pae-commons';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import useAlerts from '@/hooks/useAlerts';
import {CACHE_KEY_LIST_ACTOR_PROCESS} from '@/modules/actors/contants/process';
import {PROPOSAL_REQUEST_TCP_STEPS, PROPOSAL_REQUEST_TCP_STEPS_ORDER} from '@/modules/company/contants/procedure';

import ActorProcessApiService from '../services/ActorProcessApiService';

const {SUBJECT_TYPE} = proposalrequest;
const {PROCESS_IDENTIFIERS} = process;

export const useMutateProcessSettings = () => {
  const {t} = useTranslation('common');
  const {putErrorCode} = useAlerts();
  const queryClient = useQueryClient();
  
  const {
    mutateAsync: updateProcessSteps,
    isLoading: isUpdateProcessSteps
  } = useMutation(ActorProcessApiService.updateSteps,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_ACTOR_PROCESS);
        message.success(t('createSuccess'));
      },
      onError: (e) => {
        putErrorCode(e);
      }
    });
  
  const {mutateAsync: updateProcessField, isLoading: isUpdatingProcessField} = useMutation(({
    id,
    params
  }) => ActorProcessApiService.updateFields(id, params),
  {
    onSuccess: () => {
      message.success(t('updateSuccess'));
      queryClient.invalidateQueries(CACHE_KEY_LIST_ACTOR_PROCESS);
    },
    onError: (e) => {
      putErrorCode(e);
    }
  });
  
  const {
    mutateAsync: updateProcessPayments,
    isLoading: isUpdatingProcessPayments
  } = useMutation(ActorProcessApiService.updatePayments,
    {
      onSuccess: () => {
        message.success(t('updateSuccess'));
        queryClient.invalidateQueries(CACHE_KEY_LIST_ACTOR_PROCESS);
      },
      onError: (e) => {
        putErrorCode(e);
      }
    });
  
  return {
    updateProcessSteps,
    isUpdateProcessSteps,
    updateProcessField,
    isUpdatingProcessField,
    updateProcessPayments,
    isUpdatingProcessPayments,
  };
};

export const useGetProcessSettings = (subjectType = undefined) => {
  const {isAuthenticated} = useUser();
  const {t} = useTranslation('proposalFiles');
  
  const {
    data,
    isLoading
  } = useQuery(CACHE_KEY_LIST_ACTOR_PROCESS, () => ActorProcessApiService.search(), {
    refetchOnWindowFocus: false,
    enabled: !!isAuthenticated
  });
  
  const processKeys = {};
  const processIds = {};
  
  if (data && !isLoading) {
    data?.data?.configurations
      ?.filter((process) => {
        if (subjectType === SUBJECT_TYPE.TCP_PROPOSAL)
          return PROPOSAL_REQUEST_TCP_STEPS.includes(process?._id);
        return true;
      })
      ?.map((process) => {
        if (subjectType === SUBJECT_TYPE.TCP_PROPOSAL)
          if (process.id === PROCESS_IDENTIFIERS.COMMERCIAL_REGISTER)
            return ({...process, name: t('expedient.mercantileCertificateTCP')});
        return process;
      })
      ?.forEach((process) => {
        processKeys[process.code] = process._id;
        processIds[process._id] = process;
      });
  }
  
  return {
    processKeys,
    processIds,
    processSettings: {
      ...data?.data,
      configurations: data?.data?.configurations
        ?.filter((process) => {
          if (subjectType === SUBJECT_TYPE.TCP_PROPOSAL)
            return PROPOSAL_REQUEST_TCP_STEPS.includes(process?._id);
          return true;
        })
        ?.sort((a, b) => {
          if (subjectType === SUBJECT_TYPE.TCP_PROPOSAL) {
            // Si ambos tienen 'processIdentifier', ordena según el mapa de orden
            const orderA = PROPOSAL_REQUEST_TCP_STEPS_ORDER[a.id] ?? 999;
            const orderB = PROPOSAL_REQUEST_TCP_STEPS_ORDER[b.id] ?? 999;
            
            // Primero: por el orden de pasos TCP
            const diff = orderA - orderB;
            if (diff !== 0) return diff;
            
            // Segundo: si a está finalizado, mándalo al final
            if (a.isFinish && !b.isFinish) return 1;
            if (!a.isFinish && b.isFinish) return -1;
            
            // Tercero: por su orden numérico interno (fallback)
            return (a.order ?? 0) - (b.order ?? 0);
          }
          return (a.order ?? 0) - (b.order ?? 0);
        })
        ?.map((e, index) => {
          if (subjectType === SUBJECT_TYPE.TCP_PROPOSAL)
            return {...e, order: index + 1};
          return e;
        })
    },
    isLoadingProcessSettings: isLoading
  };
};

